<template>
  <section class="operations-tasks">
    <div class="tasks-heading">
      <div>
        <h1>Tasks</h1>
        <p>{{ isModelProfileMode ? `Tasks and due dates for ${focusedModelLabel}.` : "Today's work, follow-ups and model assignments in one place." }}</p>
      </div>
      <div class="heading-actions">
        <v-btn
          v-if="isModelProfileMode && !isModelAccount"
          variant="outlined"
          prepend-icon="mdi-calendar-month-outline"
          @click="scrollToFocusedCalendar"
        >Calendar</v-btn>
        <v-btn v-else-if="!isModelProfileMode" variant="outlined" prepend-icon="mdi-calendar-month-outline" to="/calendar">Calendar</v-btn>
        <v-btn v-if="!isModelAccount" color="primary" prepend-icon="mdi-plus" @click="isModelProfileMode ? openFocusedTaskDialog() : openTaskDialog()">Add Task</v-btn>
      </div>
    </div>

    <v-card v-if="!isModelProfileMode" class="mx-auto elevation-2 tabs-card mt-5 task-tabs-card">
      <v-container fluid>
        <v-row align="center" justify="space-between">
          <v-tabs
            v-model="activeShift"
            class="dashboard-tab"
            show-arrows
            next-icon="mdi-arrow-right-bold-box-outline"
            prev-icon="mdi-arrow-left-bold-box-outline"
            @update:model-value="selectShift"
          >
            <v-tab v-for="tab in visibleTaskTabs" :key="tab" :value="tab">{{ tab }}</v-tab>
          </v-tabs>
        </v-row>
      </v-container>
    </v-card>

    <div class="summary-grid">
      <div v-for="card in summaryCards" :key="card.key" :class="['summary-card', card.key]">
        <div class="summary-icon"><v-icon>{{ card.icon }}</v-icon></div>
        <div><span>{{ card.label }}</span><strong>{{ card.value }}</strong></div>
      </div>
    </div>

    <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mb-3">{{ errorMessage }}</v-alert>

    <template v-if="isModelProfileMode">
    <div class="task-section model-section focused-model-section">
      <div class="section-bar models">
        <span>MODEL TASKS</span>
        <small>{{ completedModelTasks }} of {{ modelTasks.length }} completed</small>
      </div>
      <div class="person-header">
        <div class="person-avatar">{{ initials(focusedModel?.stageName || focusedModel?.fullName) }}</div>
        <div><strong>{{ focusedModel?.stageName || focusedModel?.fullName || 'Model' }}</strong><small>{{ focusedModel?.fullName || '' }}</small></div>
        <v-spacer />
        <v-btn
          icon="mdi-content-copy"
          size="x-small"
          variant="text"
          color="primary"
          title="Copy model tasks"
          :disabled="!modelTasks.length"
          :loading="copyingModelId === Number(modelId)"
          @click="focusedModel && copyModelTasks(focusedModel)"
        />
        <v-btn v-if="!isModelAccount" icon="mdi-plus" size="x-small" variant="text" color="primary" @click="openFocusedTaskDialog" />
      </div>
      <TaskRows :tasks="modelTasks" :readonly="isModelAccount" empty-text="No tasks assigned." @toggle="toggleTask" @archive="archiveTask" @delete="deleteTask" @edit="openTaskForEdit" />
    </div>

    <div v-if="isModelProfileMode && focusedModel" ref="focusedCalendar" class="focused-model-calendar">
      <OperationsCalendar
        ref="focusedCalendarComponent"
        :key="Number(modelId)"
        embedded
        :focused-model-id="Number(modelId)"
        :focused-model-type="focusedModelCalendarType"
        :focused-studio-id="Number(focusedModel.studio_id || user?.studio_id)"
      />
    </div>
    </template>

    <template v-else>
    <div class="task-section">
      <div class="section-bar general"><span>GENERAL TASKS</span><small>{{ generalTasks.length }} tasks</small></div>
      <TaskRows :tasks="generalTasks" empty-text="No general tasks for this shift." @toggle="toggleTask" @archive="archiveTask" @delete="deleteTask" @edit="openTaskForEdit" />
      <button class="inline-add" @click="openTaskDialog({}, true)"><v-icon size="16">mdi-plus</v-icon>Add general task</button>
    </div>

    <template v-if="isAdmin && activeShift === 'Studio' && activeMonitors.length">
      <div class="collection-label">ACTIVE MONITORS</div>
      <div v-for="monitor in activeMonitors" :key="monitor.id" class="task-section compact-section">
        <div class="person-header">
          <div class="person-avatar monitor"><v-icon size="18">mdi-account-tie-outline</v-icon></div>
          <div><strong>{{ monitor.displayName || monitor.fullName }}</strong><small>{{ monitor.shift }} monitor</small></div>
          <v-spacer />
          <v-btn icon="mdi-plus" size="x-small" variant="text" color="primary" @click="openTaskDialog({ monitor_id: monitor.id, assigned_shift: monitor.shift })" />
        </div>
        <TaskRows :tasks="tasksForMonitor(monitor.id)" empty-text="No assigned monitor tasks." @toggle="toggleTask" @archive="archiveTask" @delete="deleteTask" @edit="openTaskForEdit" />
      </div>
    </template>

    <template v-if="activeShift !== 'Studio'">
      <div class="section-bar models"><span>{{ activeShift.toUpperCase() }} · {{ models.length }} MODELS</span><small>{{ completedModelTasks }} of {{ modelTasks.length }} completed</small></div>
      <div v-if="loading" class="loading-row"><v-progress-circular indeterminate size="24" color="primary" /> Loading tasks…</div>
      <div v-else-if="!models.length" class="empty-models">No active models found for this shift.</div>
      <div v-for="model in models" :key="model.id" class="task-section model-section">
      <div class="person-header">
        <div class="person-avatar">{{ initials(model.stageName || model.fullName) }}</div>
        <div><strong>{{ model.stageName || model.fullName }}</strong><small>{{ model.fullName }}</small></div>
        <v-spacer />
        <v-btn
          icon="mdi-content-copy"
          size="x-small"
          variant="text"
          color="primary"
          title="Copy model tasks"
          :disabled="!tasksForModel(model.id).length"
          :loading="copyingModelId === Number(model.id)"
          @click="copyModelTasks(model)"
        />
        <v-btn
          size="small"
          variant="text"
          color="primary"
          prepend-icon="mdi-calendar-month-outline"
          :to="modelCalendarLink(model)"
        >Calendar</v-btn>
        <v-btn icon="mdi-plus" size="x-small" variant="text" color="primary" @click="openTaskDialog({ model_id: model.id })" />
      </div>
      <TaskRows :tasks="tasksForModel(model.id)" empty-text="No tasks assigned." @toggle="toggleTask" @archive="archiveTask" @delete="deleteTask" @edit="openTaskForEdit" />
      </div>
    </template>
    </template>

    <v-dialog v-model="taskDialog" max-width="560">
      <v-card>
        <v-card-title class="dialog-title">{{ editingTaskId ? 'Edit Task' : 'Add Task' }}</v-card-title>
        <v-card-text class="pt-5">
          <v-text-field v-model="draft.title" label="Title" variant="outlined" density="compact" autofocus />
          <v-textarea v-model="draft.description" label="Notes (optional)" variant="outlined" density="compact" rows="2" />
          <div class="dialog-grid">
            <v-select v-model="draft.model_id" :items="modelOptions" item-title="label" item-value="id" :label="isModelProfileMode ? 'Model' : 'Model (optional)'" :clearable="!isModelProfileMode" variant="outlined" density="compact" :disabled="generalTaskDraft || isModelProfileMode" />
            <v-select v-if="draft.assigned_shift === 'Admin'" v-model="draft.assigned_user_id" :items="adminAssigneeOptions" item-title="label" item-value="id" label="Admin" variant="outlined" density="compact" />
            <v-select v-else v-model="draft.monitor_id" :items="allMonitorOptions" item-title="label" item-value="id" label="Monitor" :clearable="!isModelProfileMode" variant="outlined" density="compact" :disabled="isModelProfileMode" />
            <v-select v-model="draft.assigned_shift" :items="taskShiftOptions" label="Shift" variant="outlined" density="compact" :disabled="isModelProfileMode" @update:model-value="autofillMonitor" />
            <v-text-field v-model="draft.due_date" type="date" label="Due date (optional)" variant="outlined" density="compact" />
          </div>
          <v-alert v-if="dialogError" type="error" variant="tonal" density="compact">{{ dialogError }}</v-alert>
        </v-card-text>
        <v-card-actions class="px-6 pb-5"><v-spacer /><v-btn variant="text" @click="taskDialog = false">Cancel</v-btn><v-btn color="primary" :loading="saving" @click="saveTask">{{ editingTaskId ? 'Save Changes' : 'Save Task' }}</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="copyNotice.show" :color="copyNotice.color" timeout="2200">
      {{ copyNotice.text }}
    </v-snackbar>

    <div v-if="copyCaptureModel" ref="modelTasksCapture" class="model-tasks-copy-card" aria-hidden="true">
      <div class="model-tasks-copy-heading">
        <div>
          <span>MODATO · TAREAS</span>
          <strong>{{ copyCaptureModel.stageName || copyCaptureModel.fullName }}</strong>
          <small v-if="copyCaptureModel.stageName">{{ copyCaptureModel.fullName }}</small>
        </div>
        <div class="model-tasks-copy-shift">{{ activeShift }}</div>
      </div>
      <div class="model-tasks-copy-body">
        <div
          v-for="task in copyCaptureTasks"
          :key="task.id"
          :class="['model-tasks-copy-row', `is-${taskCopyState(task)}`]"
        >
          <div class="model-tasks-copy-status-icon">
            <v-icon size="23">{{ task.completed ? 'mdi-check-circle-outline' : 'mdi-circle-medium' }}</v-icon>
          </div>
          <div class="model-tasks-copy-content">
            <strong>{{ task.title }}</strong>
            <p v-if="task.description">{{ task.description }}</p>
          </div>
          <div class="model-tasks-copy-meta">
            <span>{{ taskCopyStatus(task) }}</span>
            <small>{{ formatTaskDate(task.due_date) }}</small>
          </div>
        </div>
      </div>
      <div class="model-tasks-copy-footer">
        <span>{{ copyCaptureTasks.length }} {{ copyCaptureTasks.length === 1 ? 'tarea' : 'tareas' }}</span>
        <span>Control Operativo</span>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent, h, resolveComponent } from 'vue';
import secureAxios from '@/utils/secureAxios';
import OperationsCalendar from '@/pages/Calendar.vue';

const TaskRows = defineComponent({
  props: {
    tasks: { type: Array, default: () => [] },
    emptyText: { type: String, default: 'No tasks.' },
    readonly: { type: Boolean, default: false },
  },
  emits: ['toggle', 'archive', 'delete', 'edit'],
  setup(props, { emit }) {
    const VIcon = resolveComponent('v-icon');
    const VList = resolveComponent('v-list');
    const VListItem = resolveComponent('v-list-item');
    const VMenu = resolveComponent('v-menu');
    const taskState = (task) => {
      if (task.completed) return 'completed';
      if (!task.due_date) return 'upcoming';
      const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(new Date());
      if (task.due_date < today) return 'overdue';
      if (task.due_date === today) return 'today';
      return 'upcoming';
    };
    const taskStatusLabel = task => ({ completed: 'Completed', overdue: 'Overdue', today: 'Due Today', upcoming: 'Upcoming' })[taskState(task)];
    return () => props.tasks.length
      ? h('div', { class: 'operational-task-list' }, props.tasks.map(task => h('div', { class: ['operational-task-row', `task-state-${taskState(task)}`, task.completed ? 'completed' : ''] }, [
        h(props.readonly ? 'span' : 'button', {
          class: 'task-check',
          onClick: props.readonly ? undefined : () => emit('toggle', task),
        }, [h('i', { class: task.completed ? 'mdi mdi-check-circle' : 'mdi mdi-checkbox-blank-circle-outline' })]),
        h(props.readonly ? 'div' : 'button', {
          class: ['task-main', props.readonly ? '' : 'task-main-button'],
          onClick: props.readonly ? undefined : () => emit('edit', task),
        }, [h('strong', task.title), task.description ? h('small', task.description) : null]),
        task.due_date ? h('span', { class: 'task-due' }, [h('i', { class: 'mdi mdi-calendar-blank-outline' }), ` ${task.due_date}`]) : null,
        h('span', { class: ['task-status', taskState(task)] }, taskStatusLabel(task)),
        props.readonly ? null : h(VMenu, {}, {
          activator: ({ props: menuProps }) => h('button', { ...menuProps, class: 'task-menu-button', 'aria-label': 'Task options' }, [h(VIcon, { size: 18 }, () => 'mdi-dots-vertical')]),
          default: () => h(VList, { density: 'compact', minWidth: 170 }, () => [
            h(VListItem, {
              title: task.completed ? 'Archive task' : 'Complete task to archive',
              prependIcon: task.completed ? 'mdi-archive-outline' : 'mdi-lock-outline',
              disabled: !task.completed,
              onClick: () => task.completed && emit('archive', task),
            }),
            h(VListItem, {
              title: 'Delete task',
              prependIcon: 'mdi-delete-outline',
              baseColor: 'error',
              onClick: () => emit('delete', task),
            }),
          ]),
        }),
      ])) )
      : h('div', { class: 'task-empty' }, props.emptyText);
  },
});

const blankDraft = shift => ({ title: '', description: '', model_id: null, monitor_id: null, assigned_user_id: null, assigned_shift: shift, due_date: '' });

export default {
  name: 'OperationalTasks',
  components: { TaskRows, OperationsCalendar },
  props: {
    modelId: { type: [Number, String], default: null },
  },
  data() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return { user, taskTabs: ['Studio', 'Morning', 'Evening', 'Night', 'Satellite'], taskShifts: ['Morning', 'Evening', 'Night', 'Satellite'], activeShift: 'Morning', tasks: [], models: [], monitors: [], adminUsers: [], loading: false, saving: false, errorMessage: '', dialogError: '', taskDialog: false, editingTaskId: null, generalTaskDraft: false, draft: blankDraft('Morning'), copyNotice: { show: false, text: '', color: 'success' }, copyingModelId: null, copyCaptureModel: null, copyCaptureTasks: [] };
  },
  computed: {
    isAdmin() { return this.user?.role === 'admin' || this.user?.isAdmin === true; },
    isSatelliteMonitor() { return this.user?.role === 'satellite_monitor'; },
    isModelAccount() { return this.user?.role === 'model'; },
    isModelProfileMode() { return Number(this.modelId) > 0; },
    focusedModel() { return this.models.find(model => Number(model.id) === Number(this.modelId)) || null; },
    focusedModelLabel() { return this.focusedModel?.stageName || this.focusedModel?.fullName || 'this model'; },
    focusedModelShift() {
      const value = String(this.focusedModel?.shift || this.focusedModel?.location || '').trim().toLowerCase();
      return this.taskShifts.find(shift => shift.toLowerCase() === value) || 'Morning';
    },
    focusedModelCalendarLink() {
      if (!this.focusedModel) return '/calendar';
      return this.modelCalendarLink(this.focusedModel);
    },
    focusedModelCalendarType() {
      return this.focusedModelShift === 'Satellite'
        ? 'sat'
        : 'studio';
    },
    visibleTaskTabs() {
      if (this.isAdmin) return this.taskTabs;
      if (this.isSatelliteMonitor) return ['Satellite'];
      return ['Morning', 'Evening', 'Night'];
    },
    taskShiftOptions() { return this.isAdmin ? ['Admin', ...this.taskShifts] : this.taskShifts; },
    activeMonitors() { return this.monitors.filter(item => item.status === 'Active'); },
    generalTasks() {
      if (this.activeShift === 'Studio') return this.tasks.filter(item => item.assigned_shift === 'Admin' && Number(item.assigned_user_id) === Number(this.user?.id));
      return this.tasks.filter(item => !item.model_id);
    },
    modelTasks() { return this.tasks.filter(item => item.model_id); },
    completedModelTasks() { return this.modelTasks.filter(item => item.completed).length; },
    modelOptions() { return this.models.map(item => ({ id: item.id, label: item.stageName || item.fullName })); },
    monitorOptions() { return this.activeMonitors.map(item => ({ id: item.id, label: item.displayName || item.fullName })); },
    allMonitorOptions() { return this.monitors.filter(item => item.status === 'Active').map(item => ({ id: item.id, label: `${item.displayName || item.fullName} · ${item.shift}` })); },
    adminAssigneeOptions() { return this.adminUsers.map(item => ({ id: item.id, label: this.adminLabel(item.email) })); },
    summaryCards() {
      const today = new Date().toISOString().slice(0, 10);
      return [
        { key: 'overdue', label: 'Overdue', icon: 'mdi-alert-circle-outline', value: this.tasks.filter(t => !t.completed && t.due_date && t.due_date < today).length },
        { key: 'today', label: 'Due Today', icon: 'mdi-clock-outline', value: this.tasks.filter(t => !t.completed && t.due_date === today).length },
        { key: 'upcoming', label: 'Upcoming', icon: 'mdi-calendar-blank-outline', value: this.tasks.filter(t => !t.completed && t.due_date && t.due_date > today).length },
        { key: 'completed', label: 'Completed', icon: 'mdi-check-circle-outline', value: this.tasks.filter(t => t.completed).length },
      ];
    },
  },
  created() { this.activeShift = this.isAdmin ? 'Studio' : this.isSatelliteMonitor ? 'Satellite' : this.currentShift(); this.loadAll(); },
  watch: {
    modelId(newValue, oldValue) {
      if (Number(newValue) !== Number(oldValue) && Number(newValue) > 0) this.loadAll();
    },
  },
  methods: {
    currentShift() { const hour = new Date().getHours(); return hour >= 22 || hour < 6 ? 'Night' : hour >= 14 ? 'Evening' : 'Morning'; },
    initials(value) { return String(value || '').split(/[\s_]+/).filter(Boolean).slice(0, 2).map(word => word[0]).join('').toUpperCase(); },
    adminLabel(email) { return String(email || '').split('@')[0].split(/[._-]+/).filter(Boolean).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' '); },
    tasksForModel(id) { return this.tasks.filter(item => Number(item.model_id) === Number(id)); },
    tasksForMonitor(id) { return this.tasks.filter(item => Number(item.monitor_id) === Number(id)); },
    formatTaskDate(value) {
      const [year, month, day] = String(value || '').slice(0, 10).split('-');
      return year && month && day ? `${day}/${month}/${year}` : 'Sin fecha';
    },
    taskCopyStatus(task) {
      if (task.completed) return 'Completada';
      if (!task.due_date) return 'Pendiente';
      const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(new Date());
      if (task.due_date < today) return 'Vencida';
      if (task.due_date === today) return 'Para hoy';
      return 'Proxima';
    },
    taskCopyState(task) {
      if (task.completed) return 'completed';
      if (!task.due_date) return 'pending';
      const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(new Date());
      if (task.due_date < today) return 'overdue';
      if (task.due_date === today) return 'today';
      return 'upcoming';
    },
    buildModelTasksCopy(model) {
      const tasks = [...this.tasksForModel(model.id)].sort((left, right) => (
        Number(left.completed) - Number(right.completed)
        || String(left.due_date || '9999-12-31').localeCompare(String(right.due_date || '9999-12-31'))
        || String(left.title || '').localeCompare(String(right.title || ''))
      ));
      const lines = [
        `*TAREAS · ${model.stageName || model.fullName}*`,
        model.stageName && model.fullName ? model.fullName : '',
        '',
      ].filter((line, index) => line || index > 1);

      tasks.forEach((task, index) => {
        const status = this.taskCopyStatus(task);
        const marker = task.completed ? '✓' : '•';
        lines.push(`${marker} *${task.title}*`);
        lines.push(`  Estado: ${status}`);
        lines.push(`  Fecha: ${this.formatTaskDate(task.due_date)}`);
        if (task.description) lines.push(`  Notas: ${String(task.description).trim()}`);
        if (index < tasks.length - 1) lines.push('');
      });
      return lines.join('\n');
    },
    async copyModelTasks(model) {
      const tasks = [...this.tasksForModel(model.id)].sort((left, right) => (
        Number(left.completed) - Number(right.completed)
        || String(left.due_date || '9999-12-31').localeCompare(String(right.due_date || '9999-12-31'))
        || String(left.title || '').localeCompare(String(right.title || ''))
      ));
      this.copyingModelId = Number(model.id);
      this.copyCaptureModel = model;
      this.copyCaptureTasks = tasks;
      try {
        await this.$nextTick();
        if (document.fonts?.ready) await document.fonts.ready;
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(this.$refs.modelTasksCapture, {
          backgroundColor: '#ffffff',
          logging: false,
          scale: 2,
          useCORS: true,
        });
        const image = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!image || !navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
          throw new Error('Image clipboard is not supported.');
        }
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': image })]);
        this.copyNotice = { show: true, text: `Task image copied for ${model.stageName || model.fullName}. Paste it into WhatsApp.`, color: 'success' };
      } catch (error) {
        try {
          await navigator.clipboard.writeText(this.buildModelTasksCopy(model));
          this.copyNotice = { show: true, text: 'Image copy is unavailable here. A text version was copied instead.', color: 'warning' };
        } catch (fallbackError) {
          this.copyNotice = { show: true, text: 'Could not copy the model tasks.', color: 'error' };
        }
      } finally {
        this.copyingModelId = null;
        this.copyCaptureModel = null;
        this.copyCaptureTasks = [];
      }
    },
    modelCalendarLink(model) {
      return {
        path: '/calendar',
        query: {
          model: String(model.id),
          type: this.activeShift === 'Satellite' ? 'sat' : 'studio',
          studio: String(model.studio_id || this.user?.studio_id || ''),
        },
      };
    },
    async selectShift(shift) { this.activeShift = shift; await this.loadAll(); },
    async loadAll() {
      this.loading = true; this.errorMessage = '';
      try {
        const studioId = Number(this.user?.studio_id);
        if (this.isModelProfileMode) {
          const [taskResponses, modelResponse, monitors] = await Promise.all([
            Promise.all(this.taskShifts.map(shift => secureAxios.get('/api/operational-tasks', {
              params: { shift, model_id: Number(this.modelId) },
            }))),
            secureAxios.get('/api/models', { params: { model_id: Number(this.modelId), studio_id: studioId } }),
            !this.isModelAccount && studioId
              ? secureAxios.get('/api/monitors', { params: { studio_id: studioId } })
              : Promise.resolve({ data: { monitors: [] } }),
          ]);
          this.tasks = taskResponses.flatMap(response => response.data || []);
          this.models = Array.isArray(modelResponse.data) ? modelResponse.data : [];
          this.monitors = monitors.data?.monitors || [];
          this.activeShift = this.focusedModelShift;
          return;
        }
        const selectedShifts = this.activeShift === 'Studio' ? ['Admin', ...this.taskShifts] : [this.activeShift];
        const [taskResponses, models, monitors, adminUsers] = await Promise.all([
          Promise.all(selectedShifts.map(shift => secureAxios.get('/api/operational-tasks', { params: { shift } }))),
          this.activeShift === 'Studio'
            ? Promise.resolve({ data: [] })
            : secureAxios.get('/api/reports/models', { params: { shift: this.activeShift, studioId } }),
          studioId ? secureAxios.get('/api/monitors', { params: { studio_id: studioId } }) : Promise.resolve({ data: { monitors: [] } }),
          this.isAdmin ? secureAxios.get('/api/operational-task-assignees') : Promise.resolve({ data: [] }),
        ]);
        this.tasks = taskResponses.flatMap(response => response.data || []); this.models = models.data || []; this.monitors = monitors.data?.monitors || []; this.adminUsers = adminUsers.data || [];
      } catch (error) { this.errorMessage = error.response?.data?.message || 'Unable to load tasks.'; }
      finally { this.loading = false; }
    },
    openFocusedTaskDialog() {
      this.openTaskDialog({ model_id: Number(this.modelId), assigned_shift: this.focusedModelShift });
    },
    scrollToFocusedCalendar() {
      this.$refs.focusedCalendar?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    },
    async refreshFocusedCalendar() {
      if (!this.isModelProfileMode) return;
      await this.$nextTick();
      await this.$refs.focusedCalendarComponent?.loadCalendar?.();
    },
    openTaskDialog(seed = {}, generalTask = false) { const defaultShift = this.activeShift === 'Studio' ? 'Admin' : this.activeShift; this.editingTaskId = null; this.generalTaskDraft = generalTask; this.draft = { ...blankDraft(defaultShift), ...seed }; this.autofillMonitor(this.draft.assigned_shift); this.dialogError = ''; this.taskDialog = true; },
    openTaskForEdit(task) { this.editingTaskId = Number(task.id); this.generalTaskDraft = !task.model_id; this.draft = { ...blankDraft(task.assigned_shift), title: task.title || '', description: task.description || '', model_id: task.model_id || null, monitor_id: task.monitor_id || null, assigned_user_id: task.assigned_user_id || null, assigned_shift: task.assigned_shift, due_date: task.due_date || '' }; if (!this.draft.monitor_id && this.draft.assigned_shift !== 'Admin') this.autofillMonitor(this.draft.assigned_shift); this.dialogError = ''; this.taskDialog = true; },
    autofillMonitor(shift) { if (shift === 'Admin') { this.draft.monitor_id = null; this.draft.model_id = null; this.draft.assigned_user_id = Number(this.user?.id) || null; return; } this.draft.assigned_user_id = null; const monitor = this.monitors.find(item => item.status === 'Active' && String(item.shift || '').toLowerCase() === String(shift || '').toLowerCase()); this.draft.monitor_id = monitor?.id || null; },
    async saveTask() {
      if (!this.draft.title.trim()) { this.dialogError = 'Title is required.'; return; }
      this.saving = true;
      try { if (this.editingTaskId) await secureAxios.put(`/api/operational-tasks/${this.editingTaskId}`, this.draft); else await secureAxios.post('/api/operational-tasks', this.draft); this.taskDialog = false; if (this.draft.assigned_shift === 'Admin') this.activeShift = 'Studio'; await this.loadAll(); await this.refreshFocusedCalendar(); }
      catch (error) { this.dialogError = error.response?.data?.message || 'Unable to save task.'; }
      finally { this.saving = false; }
    },
    async toggleTask(task) { await secureAxios.put(`/api/operational-tasks/${task.id}/complete`, { completed: !task.completed }); await this.loadAll(); await this.refreshFocusedCalendar(); },
    async archiveTask(task) { try { await secureAxios.put(`/api/operational-tasks/${task.id}/archive`); await this.loadAll(); await this.refreshFocusedCalendar(); } catch (error) { this.errorMessage = error.response?.data?.message || 'Unable to archive task.'; } },
    async deleteTask(task) { if (!window.confirm(`Delete "${task.title}"? This cannot be undone.`)) return; try { await secureAxios.delete(`/api/operational-tasks/${task.id}`); await this.loadAll(); await this.refreshFocusedCalendar(); } catch (error) { this.errorMessage = error.response?.data?.message || 'Unable to delete task.'; } },
  },
};
</script>

<style scoped>
.operations-tasks{max-width:1500px;margin:0 auto;padding:18px 18px 4px}.tasks-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;margin-bottom:14px}.tasks-heading h1{margin:0;color:#172b40;font-size:28px}.tasks-heading p{margin:4px 0 0;color:#607386;font-size:13px}.heading-actions{display:flex;gap:8px}.task-tabs-card{margin-bottom:14px}.dashboard-tab .v-tab{background-color:white;color:#2C74B3}.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:16px}.summary-card{display:flex;align-items:center;gap:13px;min-height:76px;padding:12px 16px;border:1px solid #dbe3e9;border-radius:7px;background:#fff}.summary-icon{display:grid;width:42px;height:42px;place-items:center;border-radius:50%;background:#edf5fa;color:#287ab2}.summary-card span{display:block;color:#496075;font-size:12px;font-weight:800}.summary-card strong{display:block;color:#1f3548;font-size:25px}.summary-card.overdue{background:#fff4f4}.summary-card.overdue .summary-icon,.summary-card.overdue strong{color:#cf3d46}.summary-card.upcoming{background:#fff9ef}.summary-card.upcoming .summary-icon{color:#b97816}.summary-card.completed{background:#f1faf4}.summary-card.completed .summary-icon{color:#238b4b}.task-section{overflow:hidden;margin-bottom:12px;border:1px solid #d6e0e7;border-radius:6px;background:#fff}.section-bar{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:#263f55;color:#fff;font-size:13px;font-weight:900;letter-spacing:.03em}.section-bar small{font-weight:700;opacity:.85}.section-bar.models{margin:18px 0 10px;background:#397daf}.person-header{display:flex;align-items:center;gap:10px;padding:9px 13px;border-bottom:1px solid #e2e8ed}.person-header strong,.person-header small{display:block}.person-header strong{color:#20384d;font-size:14px}.person-header small{color:#6b7f91;font-size:11px}.person-avatar{display:grid;width:36px;height:36px;place-items:center;border-radius:50%;background:#dceefa;color:#1e6f9f;font-size:12px;font-weight:900}.person-avatar.monitor{background:#e9edf2;color:#51687a}.collection-label{margin:17px 2px 7px;color:#607487;font-size:10px;font-weight:900;letter-spacing:.09em}.inline-add{display:flex;align-items:center;gap:4px;margin:6px 10px 8px;padding:4px 7px;border:0;background:transparent;color:#287ab2;font-size:12px;font-weight:800;cursor:pointer}.loading-row,.empty-models,.task-empty{padding:16px;color:#718496;font-size:12px}.loading-row{display:flex;align-items:center;gap:9px}.dialog-title{background:#263f55;color:#fff;font-size:17px;font-weight:800}.dialog-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 12px}
:deep(.operational-task-row){display:grid;grid-template-columns:34px minmax(0,1fr) auto 90px 24px;align-items:center;min-height:45px;padding:5px 10px;border-bottom:1px solid #edf1f4}:deep(.operational-task-row:last-child){border-bottom:0}:deep(.task-check){border:0;background:transparent;color:#7890a2;font-size:20px;cursor:pointer}:deep(.task-check .mdi-check-circle){color:#2b9a57}:deep(.task-main strong),:deep(.task-main small){display:block}:deep(.task-main strong){color:#253d51;font-size:13px}:deep(.task-main small){overflow:hidden;color:#718496;font-size:11px;text-overflow:ellipsis;white-space:nowrap}:deep(.task-main-button){overflow:hidden;width:100%;padding:4px 0;border:0;background:transparent;text-align:left;cursor:pointer}:deep(.task-main-button:hover strong){color:#287ab2}:deep(.task-due){margin:0 16px;color:#526a7e;font-size:11px}:deep(.task-status){padding:4px 9px;border-radius:999px;text-align:center;font-size:10px;font-weight:800}:deep(.task-status.pending){background:#e5f2fb;color:#2475aa}:deep(.task-status.done){background:#dff3e5;color:#237e45}:deep(.operational-task-row.completed .task-main){opacity:.52}:deep(.task-menu-button){display:grid;width:24px;height:28px;padding:0;place-items:center;border:0;background:transparent;color:#607789;cursor:pointer}
.model-tasks-copy-card{position:fixed;top:0;left:-10000px;width:420px;overflow:hidden;border:1px solid #cad6df;border-radius:12px;background:#fff;box-shadow:0 8px 28px rgba(24,48,67,.14);font-family:Arial,sans-serif;color:#20384d}.model-tasks-copy-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;padding:19px 20px;background:linear-gradient(135deg,#223d54,#315f80);color:#fff}.model-tasks-copy-heading>div:first-child{display:flex;min-width:0;flex-direction:column}.model-tasks-copy-heading span{font-size:10px;font-weight:800;letter-spacing:.14em;opacity:.78}.model-tasks-copy-heading strong{margin-top:5px;font-size:22px;line-height:1.12}.model-tasks-copy-heading small{margin-top:4px;font-size:12px;line-height:1.25;opacity:.82}.model-tasks-copy-shift{flex:0 0 auto;padding:6px 9px;border:1px solid rgba(255,255,255,.38);border-radius:999px;font-size:10px;font-weight:800;text-transform:uppercase}.model-tasks-copy-body{padding:12px}.model-tasks-copy-row{display:grid;grid-template-columns:30px minmax(0,1fr);align-items:start;gap:7px 9px;min-height:80px;margin-bottom:9px;padding:12px 12px 11px 10px;border:1px solid #dce5eb;border-left:5px solid #9cbed6;border-radius:7px;background:#f8fbfd}.model-tasks-copy-row:last-child{margin-bottom:0}.model-tasks-copy-status-icon{display:grid;padding-top:1px;place-items:center;color:#4d86aa}.model-tasks-copy-content{min-width:0}.model-tasks-copy-content strong{display:block;color:#243d52;font-size:15px;line-height:1.3}.model-tasks-copy-content p{margin:6px 0 0;color:#627688;font-size:13px;line-height:1.4;white-space:pre-wrap}.model-tasks-copy-meta{display:flex;grid-column:2;align-items:center;gap:8px;text-align:left}.model-tasks-copy-meta span{display:inline-flex;padding:3px 7px;border-radius:999px;background:rgba(76,130,166,.1);font-size:10px;font-weight:850}.model-tasks-copy-meta small{display:block;color:#667b8d;font-size:11px;font-weight:750}.model-tasks-copy-row.is-today{border-color:#e3c276;border-left-color:#d7ad55;background:#fff9ea}.model-tasks-copy-row.is-today .model-tasks-copy-status-icon,.model-tasks-copy-row.is-today .model-tasks-copy-meta span{color:#806019}.model-tasks-copy-row.is-today .model-tasks-copy-meta span{background:rgba(215,173,85,.14)}.model-tasks-copy-row.is-overdue{border-color:#e7b8b8;border-left-color:#d16e73;background:#fff4f4}.model-tasks-copy-row.is-overdue .model-tasks-copy-status-icon,.model-tasks-copy-row.is-overdue .model-tasks-copy-meta span{color:#9a363b}.model-tasks-copy-row.is-overdue .model-tasks-copy-meta span{background:rgba(209,110,115,.12)}.model-tasks-copy-row.is-upcoming .model-tasks-copy-meta span,.model-tasks-copy-row.is-pending .model-tasks-copy-meta span{color:#336f96}.model-tasks-copy-row.is-completed{border-color:#d8dde1;border-left-color:#aeb6bd;background:#f4f5f6;color:#747e86}.model-tasks-copy-row.is-completed .model-tasks-copy-status-icon,.model-tasks-copy-row.is-completed .model-tasks-copy-meta span{color:#7d8790}.model-tasks-copy-row.is-completed .model-tasks-copy-meta span{background:rgba(125,135,144,.11)}.model-tasks-copy-row.is-completed .model-tasks-copy-content strong{color:#737d85;opacity:.62}.model-tasks-copy-row.is-completed .model-tasks-copy-content p{color:#899198}.model-tasks-copy-footer{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;border-top:1px solid #e0e7ec;background:#f3f6f8;color:#718292;font-size:9px;font-weight:800;letter-spacing:.05em;text-transform:uppercase}
.focused-model-calendar{scroll-margin-top:16px;margin-top:18px}
.summary-card.upcoming{border-color:#7fafd1;background:#fff}.summary-card.upcoming .summary-icon,.summary-card.upcoming strong{color:#234f6d}
.summary-card.today{border-color:#d9a928;background:#fff}.summary-card.today .summary-icon,.summary-card.today strong{color:#684b00}
.summary-card.overdue{border-color:#d4777d;background:#fff}.summary-card.overdue .summary-icon,.summary-card.overdue strong{color:#7e292f}
.summary-card.completed{border-color:#56a773;background:#f1f3f5}.summary-card.completed .summary-icon,.summary-card.completed strong{color:#238b4b}
.summary-card{position:relative;overflow:hidden;border-top-width:4px;box-shadow:0 3px 10px rgba(31,53,72,.08)}
.summary-card strong,.summary-card.upcoming strong,.summary-card.today strong,.summary-card.overdue strong,.summary-card.completed strong{color:#172b40}
.summary-card .summary-icon{box-shadow:inset 0 0 0 1px currentColor}
.summary-card.upcoming .summary-icon{background:#eaf3fb;color:#397da8}
.summary-card.today .summary-icon{background:#fff2cc;color:#9a7100}
.summary-card.overdue .summary-icon{background:#fbe7e8;color:#a64148}
.summary-card.completed .summary-icon{background:#e1f2e7;color:#238b4b}
:deep(.operational-task-row),:deep(.operational-task-row.task-state-upcoming),:deep(.operational-task-row.task-state-today),:deep(.operational-task-row.task-state-overdue),:deep(.operational-task-row.task-state-completed){border-left:0;background:#fff}
:deep(.task-check .mdi-check-circle){color:#238b4b}
:deep(.task-status.upcoming){background:#d5e8f6;color:#234f6d}
:deep(.task-status.today){background:#f4d978;color:#684b00}
:deep(.task-status.overdue){background:#efc4c7;color:#7e292f}
:deep(.task-status.completed){background:#dff3e5;color:#237e45}
.model-tasks-copy-row{border-color:#7fafd1;border-left-color:#7fafd1;background:#eaf3fb}
.model-tasks-copy-row.is-upcoming .model-tasks-copy-status-icon,.model-tasks-copy-row.is-pending .model-tasks-copy-status-icon,.model-tasks-copy-row.is-upcoming .model-tasks-copy-meta span,.model-tasks-copy-row.is-pending .model-tasks-copy-meta span{color:#234f6d}
.model-tasks-copy-row.is-today{border-color:#d9a928;border-left-color:#d9a928;background:#fff2cc}
.model-tasks-copy-row.is-today .model-tasks-copy-status-icon,.model-tasks-copy-row.is-today .model-tasks-copy-meta span{color:#684b00}
.model-tasks-copy-row.is-today .model-tasks-copy-meta span{background:rgba(217,169,40,.18)}
.model-tasks-copy-row.is-overdue{border-color:#d4777d;border-left-color:#d4777d;background:#fbe7e8}
.model-tasks-copy-row.is-overdue .model-tasks-copy-status-icon,.model-tasks-copy-row.is-overdue .model-tasks-copy-meta span{color:#7e292f}
.model-tasks-copy-row.is-overdue .model-tasks-copy-meta span{background:rgba(212,119,125,.16)}
.model-tasks-copy-row.is-completed{border-color:#bec6cc;border-left-color:#bec6cc;background:#f1f3f5;color:#737d85}
@media(max-width:900px){.summary-grid{grid-template-columns:1fr 1fr}.tasks-heading{align-items:flex-start;flex-direction:column}.dialog-grid{grid-template-columns:1fr}.operations-tasks{padding:12px 8px}.heading-actions{width:100%}.heading-actions .v-btn{flex:1}}
</style>
